import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
    CheckCircle2,
    XCircle,
    Clock,
    Users,
    UserPlus,
    Loader2,
    Trash2,
    ArrowRight,
    Layers,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandItem,
    CommandList,
} from '@/components/ui/command';
import { useGetRequest, usePostRequest, useDeleteRequest } from '@/shared/hooks/requests';
import {
    SHARE_USERS,
    WORKFLOW,
    WORKFLOW_PARTICIPANTS,
    WORKFLOW_RESPOND,
} from '@/shared/utils/urls';
import { currentUserUuid, iHold } from '@/shared/auth/me.js';
import { apiErrorMessage } from '@/shared/utils/apiError';
import { SCOPE } from '@/shared/auth/scopes.js';
import { cn } from '@/lib/utils';
import { field, signingState } from '../signingState';
import { participantIsMe } from '../participants';
import { WorkflowNotice } from './WorkflowNotice';

const ParticipantStatusIcon = ({ status }) => {
    switch (status) {
        case 'signed':
            return <CheckCircle2 className="w-4 h-4 text-green-500" />;
        case 'rejected':
            return <XCircle className="w-4 h-4 text-red-500" />;
        default:
            return <Clock className="w-4 h-4 text-yellow-500" />;
    }
};

/**
 * AgreementPanel — Right side panel for files with approval_state='agreement'.
 * Shows participants list, allows owner to add/remove, allows participants to agree/reject.
 * The signed-in person (shared/auth/me.js) is "me"; people are picked from
 * the install's people directory and named by their docs uuid.
 */
export const AgreementPanel = ({ fileUuid, document }) => {
    const { t } = useTranslation(['signing']);
    const me = currentUserUuid();

    const [addPickerOpen, setAddPickerOpen] = useState(false);
    const [procedureType, setProcedureType] = useState('parallel');
    const [creating, setCreating] = useState(false);

    const getCollection = useGetRequest({ url: WORKFLOW.replace('{file_uuid}', fileUuid) });
    const createCollection = usePostRequest({ url: WORKFLOW.replace('{file_uuid}', fileUuid) });
    const addParticipants = usePostRequest({ url: WORKFLOW_PARTICIPANTS.replace('{file_uuid}', fileUuid) });
    const removeParticipants = useDeleteRequest();
    const respond = usePostRequest({ url: WORKFLOW_RESPOND.replace('{file_uuid}', fileUuid) });
    // The install's people: [{uuid, personId, displayName, email}].
    const loadUsers = useGetRequest({ url: SHARE_USERS });

    const collection = getCollection.response;
    const participants = useMemo(() => collection?.participants || [], [collection]);
    const { deadlinePassed } = signingState(collection);

    // The owner sets the agreement up, while they may send for it.
    const isOwner = useMemo(() => {
        const ownerUuid = field(document, 'owner_uuid');
        return me != null && ownerUuid != null && String(ownerUuid) === String(me);
    }, [me, document]);
    const canManage = isOwner && iHold(SCOPE.SIGNING_WRITE);
    // Voting (approve or reject) is signing's: `signing:sign`.
    const canVote = iHold(SCOPE.SIGNING_SIGN);

    const myParticipant = useMemo(
        () => participants.find((p) => participantIsMe(p, me)) || null,
        [participants, me],
    );

    const reload = () => {
        getCollection.request();
    };

    useEffect(() => {
        if (fileUuid) {
            getCollection.request();
            if (canManage) loadUsers.request();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileUuid, canManage]);

    const allUsers = (loadUsers.response || []).filter(u => {
        // exclude current user and already-added participants
        if (me != null && String(u.uuid) === String(me)) return false;
        if (participants.some(p => String(field(p, 'user_uuid')) === String(u.uuid))) return false;
        return true;
    });

    const handleCreateCollection = async () => {
        setCreating(true);
        try {
            const { success } = await createCollection.request({
                data: {
                    collectionType: 'agreement',
                    procedureType,
                    participants: [],
                },
            });
            if (success) {
                toast.success(t('signing:agreement.created'));
                reload();
            } else {
                toast.error(t('signing:agreement.createError'));
            }
        } finally {
            setCreating(false);
        }
    };

    const handleAddUser = async (user) => {
        const nextOrder = participants.length + 1;
        const { success } = await addParticipants.request({
            data: {
                participants: [{ userUuid: user.uuid, participantOrder: nextOrder }],
            },
        });
        if (success) {
            toast.success(t('signing:agreement.participantAdded'));
            reload();
        } else {
            toast.error(t('signing:agreement.addError'));
        }
    };

    const handleRemoveParticipant = async (participantUuid) => {
        const { success } = await removeParticipants.request({
            url: WORKFLOW_PARTICIPANTS.replace('{file_uuid}', fileUuid),
            data: { participantUuids: [participantUuid] },
        });
        if (success) {
            toast.success(t('signing:agreement.participantRemoved'));
            reload();
        }
    };

    const handleRespond = async (status) => {
        const { success, errorInfo } = await respond.request({ data: { status }, showError: false });
        if (success) {
            toast.success(
                status === 'signed'
                    ? t('signing:agreement.agreed')
                    : t('signing:agreement.rejected')
            );
            reload();
        } else {
            // The server's reason when it gives one (`not_workflow_participant`,
            // `ORDER_VIOLATION`, a deadline…), else the generic one; the list
            // is read again, since it was out of date.
            toast.error(errorInfo?.code ? apiErrorMessage(errorInfo) : t('signing:agreement.respondError'));
            if (errorInfo?.code === 'not_workflow_participant') reload();
        }
    };

    if (getCollection.loading && !collection) {
        return (
            <div className="flex items-center justify-center p-8">
                <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
        );
    }

    // No collection yet — show create UI for owner
    if (!collection) {
        if (!canManage) {
            return (
                <div className="p-6 text-center text-sm text-muted-foreground">
                    {t('signing:agreement.notSetup')}
                </div>
            );
        }

        return (
            <div className="p-6 space-y-4">
                <div className="text-sm font-semibold text-foreground">
                    {t('signing:agreement.createTitle')}
                </div>
                <p className="text-xs text-muted-foreground">
                    {t('signing:agreement.createDesc')}
                </p>

                <div className="space-y-2">
                    <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {t('signing:agreement.procedureType')}
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                        <button
                            type="button"
                            onClick={() => setProcedureType('parallel')}
                            className={cn(
                                "flex items-center gap-2 p-2 rounded border text-xs transition-colors",
                                procedureType === 'parallel'
                                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                                    : 'border-border hover:bg-muted'
                            )}
                        >
                            <Layers className="w-4 h-4" />
                            <span>{t('signing:mode.parallel')}</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => setProcedureType('sequential')}
                            className={cn(
                                "flex items-center gap-2 p-2 rounded border text-xs transition-colors",
                                procedureType === 'sequential'
                                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                                    : 'border-border hover:bg-muted'
                            )}
                        >
                            <ArrowRight className="w-4 h-4" />
                            <span>{t('signing:mode.sequential')}</span>
                        </button>
                    </div>
                </div>

                <Button
                    className="w-full"
                    onClick={handleCreateCollection}
                    disabled={creating}
                >
                    {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : t('signing:agreement.createBtn')}
                </Button>
            </div>
        );
    }

    // Collection exists — show participants and actions
    const agreedCount = participants.filter(p => p.status === 'signed').length;
    const rejectedCount = participants.filter(p => p.status === 'rejected').length;
    const totalCount = participants.length;

    return (
        <div className="p-6 space-y-4">
            {/* Status header */}
            <div>
                <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-foreground">
                        {t('signing:agreement.title')}
                    </h3>
                    <Badge variant={collection.isCompleted ? 'default' : 'secondary'} className={collection.isCompleted ? 'bg-green-100 text-green-700' : ''}>
                        {collection.isCompleted
                            ? t('signing:agreement.completed')
                            : t('signing:agreement.inProgress')}
                    </Badge>
                </div>
                <div className="text-xs text-muted-foreground">
                    {rejectedCount > 0
                        ? t('signing:agreement.summaryWithRejected', {
                            agreed: agreedCount,
                            rejected: rejectedCount,
                            total: totalCount,
                        })
                        : t('signing:agreement.summary', {
                            agreed: agreedCount,
                            total: totalCount,
                        })}
                </div>
                <div className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                    {collection.procedureType === 'sequential' ? (t('signing:mode.sequential')) : (t('signing:mode.parallel'))}
                </div>
            </div>

            <WorkflowNotice collection={collection} fileUuid={fileUuid} onChanged={reload} />

            {/* My response (if I'm a participant) */}
            {myParticipant && myParticipant.status === 'pending' && !canVote && (
                <p data-testid="no-vote-scope" className="text-xs text-muted-foreground border border-border rounded-lg p-3">
                    {t('signing:agreement.noVoteScope')}
                </p>
            )}
            {myParticipant && myParticipant.status === 'pending' && canVote && (
                <div className="border border-blue-200 bg-blue-50 rounded-lg p-3 space-y-2">
                    <p className="text-xs text-blue-700 font-medium">
                        {t('signing:agreement.yourResponseNeeded')}
                    </p>
                    <div className="flex gap-2">
                        <Button
                            size="sm"
                            className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                            onClick={() => handleRespond('signed')}
                            disabled={respond.loading || deadlinePassed}
                        >
                            {respond.loading ? <Loader2 className="w-3 h-3 animate-spin" /> : t('signing:agreement.agreeBtn')}
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 text-red-600 border-red-300 hover:bg-red-50"
                            onClick={() => handleRespond('rejected')}
                            disabled={respond.loading || deadlinePassed}
                        >
                            {t('signing:agreement.rejectBtn')}
                        </Button>
                    </div>
                </div>
            )}

            {myParticipant && myParticipant.status !== 'pending' && (
                <div className={cn(
                    "border rounded-lg p-3 text-xs font-medium flex items-center gap-2",
                    myParticipant.status === 'signed'
                        ? 'border-green-200 bg-green-50 text-green-700'
                        : 'border-red-200 bg-red-50 text-red-700'
                )}>
                    <ParticipantStatusIcon status={myParticipant.status} />
                    <span>
                        {myParticipant.status === 'signed'
                            ? t('signing:agreement.youAgreed')
                            : t('signing:agreement.youRejected')}
                    </span>
                </div>
            )}

            {/* Participants list */}
            <div className="space-y-2">
                <div className="flex items-center justify-between">
                    <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {t('signing:agreement.participants')} ({totalCount})
                    </label>
                </div>

                <div className="space-y-1">
                    {participants.length === 0 && (
                        <p className="text-xs text-muted-foreground text-center py-4">
                            {t('signing:agreement.noParticipants')}
                        </p>
                    )}
                    {participants.map((p) => (
                        <div
                            key={p.uuid}
                            className="flex items-center gap-2 p-2 rounded-md bg-muted/30 border border-border/50"
                        >
                            <ParticipantStatusIcon status={p.status} />
                            <div className="flex-1 min-w-0">
                                <div className="text-xs font-medium truncate">
                                    {p.user?.displayName ||
                                        p.user?.email ||
                                        t('signing:agreement.unnamedPerson')}
                                </div>
                                {collection.procedureType === 'sequential' && (
                                    <div className="text-[10px] text-muted-foreground">
                                        #{p.participantOrder}
                                    </div>
                                )}
                            </div>
                            {canManage && p.status === 'pending' && (
                                <button
                                    onClick={() => handleRemoveParticipant(p.uuid)}
                                    className="p-1 hover:bg-red-50 rounded text-red-500"
                                    title={t('signing:addSigners.removeSigner')}
                                >
                                    <Trash2 className="w-3 h-3" />
                                </button>
                            )}
                        </div>
                    ))}
                </div>

                {/* Add participant button (owner only) */}
                {canManage && (
                    <Popover open={addPickerOpen} onOpenChange={setAddPickerOpen}>
                        <PopoverTrigger asChild>
                            <Button variant="outline" size="sm" className="w-full gap-2">
                                <UserPlus className="w-4 h-4" />
                                {t('signing:agreement.addParticipant')}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[260px] p-0 z-[300]" align="start">
                            <Command>
                                <CommandList className="max-h-[260px]">
                                    <CommandEmpty>
                                        {t('signing:agreement.noUsersFound')}
                                    </CommandEmpty>
                                    <CommandGroup heading={t('signing:mode.people')}>
                                        {allUsers.map((user) => (
                                            <CommandItem
                                                key={user.uuid}
                                                value={`${user.displayName || ''} ${user.email || ''} ${user.uuid}`}
                                                onSelect={() => {
                                                    handleAddUser(user);
                                                    setAddPickerOpen(false);
                                                }}
                                                className="cursor-pointer"
                                            >
                                                <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-xs font-medium text-blue-700 mr-2">
                                                    {(user.displayName || user.email || 'U')[0].toUpperCase()}
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="text-sm truncate">{user.displayName || user.email}</p>
                                                    {user.displayName && user.email && (
                                                        <p className="text-[11px] text-muted-foreground truncate">{user.email}</p>
                                                    )}
                                                </div>
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </CommandList>
                            </Command>
                        </PopoverContent>
                    </Popover>
                )}
            </div>
        </div>
    );
};
